import type {
  ColumnConfig,
  FilterValue,
  TableRowData,
  FilterableColumnConfig,
  TableQueryState,
} from '../types';
import type { FilterOperator } from '../types';
import { ColumnDataTypes } from '../types';
import {
  isValidOperatorForDataType,
  getOperatorsForDataType,
  isValidNumericFilterRange,
} from '@kirin/api-client/contracts/table';
import { getLeafColumns } from './columnGroupUtils';

/**
 * Type guard to check if a column is filterable
 */
function isFilterableColumn<TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): column is FilterableColumnConfig<TRow> {
  return column.filterable === true;
}

/**
 * Validates that a column's filterOperators are valid for its dataType.
 * Returns an array of invalid operators (empty if all are valid).
 */
export function validateColumnOperators<TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): FilterOperator[] {
  if (!isFilterableColumn(column)) {
    return [];
  }

  const validOperators = getOperatorsForDataType(column.dataType);
  if (!validOperators || validOperators.length === 0) {
    return [];
  }

  const invalidOperators: FilterOperator[] = [];
  for (const op of column.filterOperators) {
    if (!validOperators.includes(op.value)) {
      invalidOperators.push(op.value);
    }
  }

  return invalidOperators;
}

/**
 * Validates all columns and logs warnings for any with invalid operators.
 * Returns true if all columns are valid.
 */
export function validateColumnsOperators<TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): boolean {
  let allValid = true;

  for (const column of getLeafColumns(columns)) {
    if (!isFilterableColumn(column)) {
      continue;
    }

    const invalidOperators = validateColumnOperators(column);
    if (invalidOperators.length > 0) {
      console.warn(
        `[DataTable] Column "${column.id}" has dataType "${column.dataType}" but uses invalid operators: ${invalidOperators.join(', ')}. ` +
          `Valid operators for ${column.dataType}: ${getOperatorsForDataType(column.dataType).join(', ')}`
      );
      allValid = false;
    }
  }

  return allValid;
}

/**
 * Drops hydrated filters whose column is missing or not filterable, whose operator
 * is invalid, or whose NUMBER value isn't a valid [min, max] range.
 */
export function filterValidFilters<TRow extends TableRowData>(
  filters: FilterValue[],
  columns: ColumnConfig<TRow>[]
): FilterValue[] {
  // Build a map of filterable columns by ID
  const filterableMap = new Map<string, FilterableColumnConfig<TRow>>();

  for (const col of getLeafColumns(columns)) {
    if (isFilterableColumn(col)) {
      filterableMap.set(col.id, col);
    }
  }

  return filters.filter(filter => {
    const column = filterableMap.get(filter.field);
    if (!column) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: column not found or not filterable`
      );
      return false;
    }

    const dataType = column.dataType;
    if (!dataType) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: column has no dataType`
      );
      return false;
    }

    const isValid = isValidOperatorForDataType(dataType, filter.operator);
    if (!isValid) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: operator "${filter.operator}" is not valid for dataType "${dataType}"`
      );
      return false;
    }

    // Also check if the operator is in the column's allowed operators
    const filterOperators = column.filterOperators;
    if (!filterOperators) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: column has no filterOperators`
      );
      return false;
    }

    const allowedOperators = filterOperators.map(
      (op: { value: FilterOperator }) => op.value
    );
    if (!allowedOperators.includes(filter.operator)) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: operator "${filter.operator}" is not in column's filterOperators`
      );
      return false;
    }

    if (
      dataType === ColumnDataTypes.NUMBER &&
      !isValidNumericFilterRange(filter.value)
    ) {
      console.warn(
        `[DataTable] Filter for field "${filter.field}" dropped: value is not a valid [min, max] numeric range`
      );
      return false;
    }

    return true;
  });
}

/** Scrubs invalid filters from a hydrated state, leaving everything else untouched. */
export function hydrateWithValidFilters<TRow extends TableRowData>(
  state: TableQueryState,
  columns: ColumnConfig<TRow>[]
): TableQueryState {
  return {
    ...state,
    filters: filterValidFilters(state.filters, columns),
  };
}
